/**
 * Railway Integration Test Component
 */
import { useTabbedChrome } from '@/components/weavein';
import React, { useState } from 'react';

export default function RailwayTest() {
  const tabbed = useTabbedChrome();
  const [railwayUrl, setRailwayUrl] = useState('https://trust-marketplace-backend-production-abc123.up.railway.app');
  const [testResults, setTestResults] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const testRailwayAPI = async () => {
    setTesting(true);
    setTestResults(null);

    try {
      const results = {
        url: railwayUrl,
        tests: []
      };

      // Test health endpoint
      try {
        const healthResponse = await fetch(`${railwayUrl}/health`);
        const healthData = await healthResponse.json();
        results.tests.push({
          name: 'Health Check',
          url: `${railwayUrl}/health`,
          status: healthResponse.ok ? 'success' : 'error',
          data: healthData
        });
      } catch (error) {
        results.tests.push({
          name: 'Health Check',
          url: `${railwayUrl}/health`,
          status: 'error',
          error: error.message
        });
      }

      // Test root endpoint
      try {
        const rootResponse = await fetch(`${railwayUrl}/`);
        const rootData = await rootResponse.json();
        results.tests.push({
          name: 'Root Endpoint',
          url: `${railwayUrl}/`,
          status: rootResponse.ok ? 'success' : 'error',
          data: rootData
        });
      } catch (error) {
        results.tests.push({
          name: 'Root Endpoint',
          url: `${railwayUrl}/`,
          status: 'error',
          error: error.message
        });
      }

      // Test API endpoints
      try {
        const usersResponse = await fetch(`${railwayUrl}/api/users`);
        const usersData = await usersResponse.json();
        results.tests.push({
          name: 'Users API',
          url: `${railwayUrl}/api/users`,
          status: usersResponse.ok ? 'success' : 'error',
          data: usersData
        });
      } catch (error) {
        results.tests.push({
          name: 'Users API',
          url: `${railwayUrl}/api/users`,
          status: 'error',
          error: error.message
        });
      }

      setTestResults(results);
    } catch (error) {
      setTestResults({
        url: railwayUrl,
        error: error.message,
        tests: []
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {!tabbed && <h1 className="text-2xl font-bold mb-4">Railway Backend Integration Test</h1>}
      
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">
          Railway Service URL:
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={railwayUrl}
            onChange={(e) => setRailwayUrl(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://your-service.railway.app"
          />
          <button
            onClick={testRailwayAPI}
            disabled={testing}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {testing ? 'Testing...' : 'Test API'}
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Get your Railway URL from the Railway dashboard → Your Service → Settings → Domains
        </p>
      </div>

      {testing && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2">Testing Railway API...</p>
        </div>
      )}

      {testResults && (
        <div className="space-y-4">
          <div className={`p-4 rounded-md ${
            testResults.tests.every((test: any) => test.status === 'success') 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <h3 className={`font-semibold ${
              testResults.tests.every((test: any) => test.status === 'success') 
                ? 'text-green-800' 
                : 'text-yellow-800'
            }`}>
              {testResults.tests.every((test: any) => test.status === 'success') 
                ? '✅ All Tests Passed!' 
                : '⚠️ Some Tests Failed'}
            </h3>
            <p className={`text-sm ${
              testResults.tests.every((test: any) => test.status === 'success') 
                ? 'text-green-600' 
                : 'text-yellow-600'
            }`}>
              Testing URL: {testResults.url}
            </p>
          </div>

          <div className="space-y-3">
            {testResults.tests.map((test: any, index: number) => (
              <div key={index} className="border rounded p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">{test.name}</h4>
                  <span className={`px-2 py-1 rounded text-sm ${
                    test.status === 'success' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {test.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">URL: {test.url}</p>
                <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
                  {JSON.stringify(test.data || test.error, null, 2)}
                </pre>
              </div>
            ))}
          </div>

          {testResults.tests.every((test: any) => test.status === 'success') && (
            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Next Steps:</h4>
              <ol className="text-sm text-blue-600 space-y-1">
                <li>1. Copy the working URL above</li>
                <li>2. Create a <code>.env.local</code> file with: <code>VITE_API_URL=your-railway-url</code></li>
                <li>3. Restart your development server</li>
                <li>4. Your frontend will now use the Railway backend!</li>
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
